<template>
  <div class="inventory-side-menu inventory-side-menu--glass">
    <div class="inventory-side-menu-header">
      <h3 class="inventory-side-menu-title">Inventory Items</h3>
      <div class="inventory-side-menu-count">{{ filteredInventoryItems.length }} item{{ filteredInventoryItems.length !== 1 ? 's' : '' }}</div>
    </div>

    <div
      class="inventory-side-menu-search"
      :class="{ 'inventory-side-menu-search--has-badge': searchQuery && filteredInventoryItems.length > 0 }"
    >
      <div class="inventory-side-menu-search-field">
        <span class="inventory-side-menu-search-icon" aria-hidden="true">
          <i class="bi bi-search"></i>
        </span>
        <input
          :value="searchQuery"
          class="inventory-side-menu-search-input"
          type="text"
          placeholder="Search inventory item..."
          @input="$emit('search-changed', $event.target.value)"
        />
      </div>
      <span v-if="searchQuery && filteredInventoryItems.length > 0" class="search-results-badge">
        {{ filteredInventoryItems.length }} found
      </span>
    </div>

    <div class="inventory-role-filter" role="group" aria-label="Filter by item type">
      <button
        v-for="option in roleFilterOptions"
        :key="option.value"
        type="button"
        class="inventory-role-filter-chip"
        :class="[
          `inventory-role-filter-chip--${option.value}`,
          { 'is-active': roleFilter === option.value },
        ]"
        :aria-pressed="roleFilter === option.value"
        :title="`${option.label} (${option.count})`"
        :aria-label="`${option.label} (${option.count})`"
        @click="roleFilter = option.value"
      >
        <i v-if="option.icon" :class="option.icon" aria-hidden="true"></i>
        <span v-else class="inventory-role-filter-all">All</span>
        <span class="inventory-role-filter-count">{{ option.count }}</span>
      </button>
    </div>

    <div class="inventory-side-menu-table-container">
      <table class="inventory-side-menu-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Qty</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td colspan="2">Loading...</td>
          </tr>
          <tr v-else-if="filteredInventoryItems.length === 0">
            <td colspan="2" class="inventory-side-menu-no-items">
              {{ roleFilter !== 'all' && statusAndSearchFilteredItems.length ? 'No items of this type' : 'No inventory items yet' }}
            </td>
          </tr>
          <tr
            v-for="item in filteredInventoryItems"
            :key="item.id"
            :class="[
              { 'inventory-side-menu-selected': selectedItem && selectedItem.id === item.id },
              item.role ? `inventory-role-row inventory-role-row--${item.role}` : '',
            ]"
            @click="selectItem(item.id)"
          >
            <td>
              <span class="inventory-side-menu-item-icon" aria-hidden="true" :title="item.roleLabel">
                <i :class="item.roleIcon || 'bi bi-box-seam'"></i>
              </span>
              <span class="inventory-role-name-stack">
                <span class="inventory-side-menu-item-name">{{ item.name }}</span>
                <span
                  v-if="item.role"
                  class="inventory-role-type"
                  :class="`inventory-role-type--${item.role}`"
                >{{ item.roleLabel }}</span>
              </span>
            </td>
            <td class="inventory-side-menu-qty">{{ item.qty }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InventorySideMenu',
  props: {
    inventoryItems: {
      type: Array,
      default: () => [],
    },
    selectedItem: {
      type: Object,
      default: null,
    },
    inventoryFilter: {
      type: String,
      default: 'active',
    },
    searchQuery: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['item-selected', 'search-changed'],
  data() {
    return {
      roleFilter: 'all',
    };
  },
  computed: {
    statusAndSearchFilteredItems() {
      let filtered = this.inventoryItems;
      if (this.inventoryFilter === 'active') {
        filtered = filtered.filter((item) => item.status === 'active');
      } else if (this.inventoryFilter === 'inactive') {
        filtered = filtered.filter((item) => item.status === 'inactive');
      }
      if (this.searchQuery) {
        filtered = filtered.filter((item) => (item.name || '').toLowerCase().includes(this.searchQuery.toLowerCase()));
      }
      return filtered;
    },
    filteredInventoryItems() {
      if (this.roleFilter === 'all') return this.statusAndSearchFilteredItems;
      return this.statusAndSearchFilteredItems.filter((item) => item.role === this.roleFilter);
    },
    roleFilterOptions() {
      const items = this.statusAndSearchFilteredItems;
      const countOf = (role) => items.filter((item) => item.role === role).length;
      return [
        { value: 'all', label: 'All', icon: '', count: items.length },
        { value: 'finished', label: 'Finished goods', icon: 'bi bi-box-seam-fill', count: countOf('finished') },
        { value: 'part', label: 'Components', icon: 'bi bi-puzzle', count: countOf('part') },
        { value: 'resale', label: 'Resale items', icon: 'bi bi-arrow-left-right', count: countOf('resale') },
      ];
    },
  },
  methods: {
    selectItem(itemId) {
      this.$emit('item-selected', itemId);
    },
  },
};
</script>

<style scoped>
@import '../styles/InventorySideMenu.css';
@import '../styles/inventory-side-menu-glass.css';
</style>
